import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Apple, Cookie, Droplet, Dumbbell,
  Heart, Plus, LineChart
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { healthService } from '../../services/database/health';
import { format } from 'date-fns';
import { toast } from 'sonner';

const WEEK_DAYS = ['S', 'T', 'Q', 'Q', 'S', 'S'];

export const WellnessTracker: React.FC = () => {
  const { user } = useAuth();
  const [healthLog, setHealthLog] = useState<any>(null);
  const [workoutSessions, setWorkoutSessions] = useState<Set<number>>(new Set());
  const [selfCareSessions, setSelfCareSessions] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);

  const today = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    if (user) {
      loadHealthData();
    }
  }, [user]);

  const loadHealthData = async () => {
    try {
      const [health, workouts, selfCare] = await Promise.all([
        healthService.getHealthLog(user!.id, today),
        healthService.getWorkoutSessions(user!.id, today),
        healthService.getSelfCareSessions(user!.id, today)
      ]);

      setHealthLog(health);
      setWorkoutSessions(new Set(workouts.filter(w => w.completed).map(w => w.day_of_week)));
      setSelfCareSessions(new Set(selfCare.filter(s => s.completed).map(s => s.day_of_week)));
    } catch (error) {
      console.error('Error loading health data:', error);
      toast.error('Erro ao carregar dados de saúde');
    } finally {
      setLoading(false);
    }
  };

  const toggleDietItem = async (item: 'ate_well' | 'ate_sweets' | 'drank_water') => {
    if (!user) return;

    try {
      const updates = {
        [item]: healthLog ? !healthLog[item] : true
      };

      const updated = await healthService.updateHealthLog(user.id, today, updates);
      setHealthLog(updated);
      toast.success('Atualizado com sucesso!');
    } catch (error) {
      console.error('Error updating health log:', error);
      toast.error('Erro ao atualizar');
    }
  };

  const toggleWorkout = async (day: number) => {
    if (!user) return;

    try {
      await healthService.toggleWorkoutSession(user.id, today, day);
      setWorkoutSessions(prev => {
        const next = new Set(prev);
        if (next.has(day)) {
          next.delete(day);
        } else {
          next.add(day);
        }
        return next;
      });
      toast.success('Atualizado com sucesso!');
    } catch (error) {
      console.error('Error toggling workout:', error);
      toast.error('Erro ao atualizar');
    }
  };

  const toggleSelfCare = async (day: number) => {
    if (!user) return;

    try {
      await healthService.toggleSelfCareSession(user.id, today, day);
      setSelfCareSessions(prev => {
        const next = new Set(prev);
        if (next.has(day)) {
          next.delete(day);
        } else {
          next.add(day);
        }
        return next;
      });
      toast.success('Atualizado com sucesso!');
    } catch (error) {
      console.error('Error toggling self care:', error);
      toast.error('Erro ao atualizar');
    }
  };

  const dietItems = [
    { 
      id: 'ate_well' as const, 
      icon: <Apple className="w-5 h-5" />, 
      label: 'Alimentação Saudável',
      checked: healthLog?.ate_well
    },
    { 
      id: 'ate_sweets' as const, 
      icon: <Cookie className="w-5 h-5" />, 
      label: 'Doces',
      checked: healthLog?.ate_sweets
    },
    { 
      id: 'drank_water' as const, 
      icon: <Droplet className="w-5 h-5" />, 
      label: 'Água',
      checked: healthLog?.drank_water
    },
  ];

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-[28px] font-semibold text-white">Saúde</h2>
        <button 
          className="flex items-center gap-2 py-3 px-5 bg-white text-black text-sm font-medium rounded-2xl hover:bg-[rgb(229,229,229)] transition-all duration-200"
          onClick={() => {
            const weight = prompt('Digite seu peso:');
            if (weight && user) {
              healthService.updateHealthLog(user.id, today, { weight: parseFloat(weight) });
            }
          }}
        >
          <Plus className="w-4 h-4" />
          <span>Adicionar peso</span>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Dieta Section */}
        <div className="bg-[rgb(28,28,30)] rounded-2xl p-6 space-y-4">
          <h3 className="text-[20px] font-semibold text-white">Dieta</h3>
          <div className="space-y-3">
            {dietItems.map(item => (
              <div 
                key={item.id}
                onClick={() => toggleDietItem(item.id)}
                className="flex items-center gap-3 p-3 bg-[rgb(44,44,46)] rounded-2xl hover:bg-[rgb(60,60,62)] transition-all duration-200 cursor-pointer group"
              >
                <div className="p-2 rounded-2xl bg-[rgb(60,60,62)] group-hover:bg-[rgb(70,70,72)] transition-all duration-200">
                  {item.icon}
                </div>
                <span className="text-[17px] text-white">{item.label}</span>
                <div className="ml-auto">
                  <div className={`
                    w-6 h-6 rounded-full border-2 transition-all duration-200 flex items-center justify-center
                    ${item.checked 
                      ? 'bg-white border-white' 
                      : 'border-white/20 group-hover:border-white/30'
                    }
                  `}>
                    {item.checked && (
                      <div className="w-3 h-3 rounded-full bg-black" />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Workout Section */}
        <div className="bg-[rgb(28,28,30)] rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Dumbbell className="w-6 h-6" />
            <h3 className="text-[20px] font-semibold text-white">Academia</h3>
            <span className="ml-auto text-[15px] text-[rgb(152,152,157)]">
              {workoutSessions.size}/6
            </span>
          </div>
          <div className="grid grid-cols-6 gap-2">
            {WEEK_DAYS.map((day, index) => (
              <button
                key={index}
                onClick={() => toggleWorkout(index)}
                className={`
                  w-full aspect-square rounded-2xl flex items-center justify-center text-[15px] font-medium
                  ${workoutSessions.has(index)
                    ? 'bg-white text-black'
                    : 'bg-[rgb(44,44,46)] text-white hover:bg-[rgb(60,60,62)]'
                  }
                  transition-all duration-200
                `}
              >
                {day}
              </button>
            ))}
          </div>
        </div>

        {/* Self Care Section */}
        <div className="bg-[rgb(28,28,30)] rounded-2xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6" />
            <h3 className="text-[20px] font-semibold text-white">Cuidados</h3>
            <span className="ml-auto text-[15px] text-[rgb(152,152,157)]">
              {selfCareSessions.size}/6
            </span>
          </div>
          <div className="grid grid-cols-6 gap-2">
            {WEEK_DAYS.map((day, index) => (
              <button
                key={index}
                onClick={() => toggleSelfCare(index)}
                className={`
                  w-full aspect-square rounded-2xl flex items-center justify-center text-[15px] font-medium
                  ${selfCareSessions.has(index)
                    ? 'bg-white text-black'
                    : 'bg-[rgb(44,44,46)] text-white hover:bg-[rgb(60,60,62)]'
                  }
                  transition-all duration-200
                `}
              >
                {day}
              </button>
            ))}
          </div>
        </div>

        {/* Weekly Progress */}
        <div className="bg-[rgb(28,28,30)] rounded-2xl p-6 space-y-4">
          <h3 className="text-[20px] font-semibold text-white">Evolução Semanal</h3>
          <div className="flex items-center gap-4 text-[15px] text-[rgb(152,152,157)]">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span>Peso (kg)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>Treinos</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500" />
              <span>Cuidados</span>
            </div>
          </div>
          <div className="h-[150px] flex items-end justify-between">
            {WEEK_DAYS.map((day, index) => {
              const hasWorkout = workoutSessions.has(index);
              const hasSelfCare = selfCareSessions.has(index);
              const weight = healthLog?.weight || 0;

              return (
                <div key={index} className="flex flex-col items-center gap-2 flex-1">
                  <div className="w-full px-1 space-y-1">
                    {weight > 0 && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: '33%' }}
                        className="w-full bg-gradient-to-t from-blue-500/20 to-blue-500/5 rounded-2xl"
                      />
                    )}
                    {hasWorkout && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: '33%' }}
                        className="w-full bg-gradient-to-t from-green-500/20 to-green-500/5 rounded-2xl"
                      />
                    )}
                    {hasSelfCare && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: '33%' }}
                        className="w-full bg-gradient-to-t from-purple-500/20 to-purple-500/5 rounded-2xl"
                      />
                    )}
                  </div>
                  <span className="text-[13px] text-[rgb(152,152,157)]">
                    {day}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Stats at Bottom */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[rgb(28,28,30)] rounded-2xl p-4 text-center">
          <div className="text-[15px] text-[rgb(152,152,157)]">Alimentação</div>
          <div className="text-[20px] font-semibold text-white mt-1">
            {healthLog?.ate_well ? '1' : '0'}/1
          </div>
          <div className="text-[13px] text-[rgb(152,152,157)]">dias saudáveis</div>
        </div>
        <div className="bg-[rgb(28,28,30)] rounded-2xl p-4 text-center">
          <div className="text-[15px] text-[rgb(152,152,157)]">Academia</div>
          <div className="text-[20px] font-semibold text-white mt-1">
            {Math.round((workoutSessions.size / 6) * 100)}%
          </div>
          <div className="text-[13px] text-[rgb(152,152,157)]">de conclusão</div>
        </div>
        <div className="bg-[rgb(28,28,30)] rounded-2xl p-4 text-center">
          <div className="text-[15px] text-[rgb(152,152,157)]">Cuidados</div>
          <div className="text-[20px] font-semibold text-white mt-1">
            {Math.round((selfCareSessions.size / 6) * 100)}%
          </div>
          <div className="text-[13px] text-[rgb(152,152,157)]">de conclusão</div>
        </div>
      </div>
    </div>
  );
};